// @loom/shared/figma/scene — the drawing's data contract, and NOTHING else.
//
// A `DesignScene` is a flat draw list: one entry per shape, in paint order, in
// the frame's own coordinates. It is what the browser receives when it asks
// "show me the screen Loom read". It is produced in exactly one place
// (`projectDesignScene`, `./project.ts`) and consumed in exactly one place
// (the Focus preview widget).
//
// ── Why this file imports nothing from `./project.ts` ─────────────────────
// The dependency runs one way on purpose: `project.ts` imports this module,
// never the reverse. `apps/web` has never carried `@loom/shared/figma` — its
// shared subpaths are `.`, `/admin`, `/analytics`, `/code-map`, `/config`,
// `/dashboard`, `/readiness`, `/session`, `/spend` — and `@loom/shared/figma`
// resolves to `./index.ts`, which re-exports the projector plus `handoff`,
// `fence` and `sanitize`. `designSceneSchema` is a VALUE, so an `import type`
// would not save the bundle from any of it. Hence the dedicated
// `@loom/shared/figma/scene` subpath, and hence this module's one hard rule:
// it may import zod, and nothing else from this package.
//
// ── What is NOT on the wire, and why that is the point ────────────────────
// NO LAYER NAME. Not one. The icon match runs on the SERVER and only the
// matched primitive id — a member of the closed {@link ICON_PRIMITIVES} enum
// Loom owns — travels. An unmatched name ships as a plain box carrying no
// string at all. So the only WORDS the browser ever receives are `lines`: the
// text the design actually DISPLAYS, which is exactly what a screenshot would
// show, and which rides the same live-only lane the Figma chip label already
// does. A layer named "Verbinde dein GitLab-Konto erneut" never reaches a
// client.
//
// The two other strings a node may carry are SHAPES, not words: `d` and `sd`,
// a vector's own outline as SVG path data. Their bytes are Figma's geometry
// serialiser's, re-spelled by the projector as commands and rounded numbers —
// never a string a designer typed. {@link SCENE_PATH_RE} admits the path
// command letters, digits and separators and nothing else: no `<`, no `(`, no
// `#`, no `:`, so a path cannot carry a tag, a `url(...)`, a colour or a
// scheme, and the regex is asserted at both ends of the wire.
//
// Every object schema here is `.strict()`, so that rule is enforced by the
// parse rather than by review: a `name` key on a scene node is a parse
// failure, not a leak.
//
// ── Where a scene may go, and where it may never ──────────────────────────
// A scene IS content — it carries the words on someone's screen and the
// arrangement of an unreleased design. It rides the bus as its own
// `figma_scene` event and therefore PERSISTS, in the two encrypted tables that
// already hold this read's digest: `app.session_events` (the chat's own 30-day
// horizon) and `app.session_log` (each batch's own 30 days). That is the whole
// point — a restored chat has to show the picture that conversation had, not
// today's copy of the frame — and it ships with the disclosure that says so.
//
// What has NOT moved: no field of a scene may ever reach a LOG LINE or an
// action-ledger row. Those are content-free surfaces and this is content.
// Nothing in this module logs; the serve route logs counts and a duration and
// nothing off the draw list; the action recorder names `figma_scene` in an
// explicit `return`.
//
// LOOKBEHIND-FREE, and it must stay that way: this module ships to the browser
// through the `apps/web` bundle, whose Vite target is pinned to `safari14`,
// where `(?<` throws at RUNTIME and passes every vitest run.

import { z } from "zod";

// ─── Version ─────────────────────────────────────────────────────────────────

/**
 * The draw-list shape's own version.
 *
 * Bumped when a renderer written against the old shape would draw the new one
 * WRONG — a changed field meaning, a changed coordinate space, a retired kind.
 * An additive optional field does not move it.
 *
 * 2 is outlines and gradients. Additive on the wire, and still a bump, because
 * a version-1 renderer draws version 2 wrong rather than merely plainer: it
 * returns nothing for the new `path` kind, so every vector Loom holds an
 * outline for would VANISH from the picture; and a gradient now arrives as
 * `grad` with no `fill`, which a version-1 renderer draws as an empty
 * rectangle. `counts.plainBoxes` also changed meaning — it counts vectors with
 * no drawable outline, and no longer counts a gradient Loom can draw — so a
 * caption written against 1 would state the wrong reason.
 *
 * A renderer reads it to refuse a shape it was not written for, and a bump is
 * the signal to drop any in-process cached drawing rather than serve a shape
 * the new renderer cannot read (the serve route's LRU is keyed by the design
 * row and lives two minutes, so a release turns it over on its own).
 *
 * THERE IS A PERSISTED CORPUS NOW. Scenes live in chat histories
 * (`figma_scene`, up to the session's 30-day horizon), so a bump leaves every
 * stored scene carrying the OLD number: a renderer refuses those and falls back
 * to `GET /api/sessions/:id/figma/preview/:previewId`, which re-projects the
 * same design-store bytes with the current projector and answers in the current
 * shape. So a bump degrades an old chat to the pre-change behaviour — the
 * newest bytes, dated — rather than breaking it, and it costs no Figma call.
 * Still deliberately separate from `DESIGN_PROJECTOR_VERSION`, which is the
 * expensive one: that number demotes rows in the design store itself.
 */
export const SCENE_VERSION = 2;

// ─── Bounds ──────────────────────────────────────────────────────────────────

/**
 * The largest frame that may become a drawing, on either axis.
 *
 * A hostile or corrupt blob can claim any box it likes, and a `w: 99999` node
 * that reached an SVG attribute would paint over the Focus column. Every node
 * is intersected with the frame server-side and this bounds the frame itself,
 * so the two together make "the drawing cannot escape its box" arithmetic
 * rather than CSS. The client still sets `overflow: hidden` — belt and braces,
 * not the guard.
 */
export const SCENE_MAX_DIMENSION = 20000;

/**
 * The most shapes one drawing may carry.
 *
 * Equal to `FIGMA_MAX_NODES` by intent, not by import (this module may not
 * reach into the projector). A design over it is REFUSED — no scene at all —
 * and never drawn with a silent hole: a drawing missing its bottom half reads
 * as "the design is empty here", which is the exact false void the preview
 * exists to avoid. The refusal has user-facing copy already
 * (`focus.figma.preview.noDrawing`); a silent hole has none, and could not
 * honestly have one, because nothing in the payload could say WHERE the hole
 * is.
 */
export const SCENE_MAX_NODES = 800;

/** The most wrapped lines one text node may emit. A bound on the payload, not
 *  a design rule: past this a paragraph is a wall either way. */
export const SCENE_MAX_TEXT_LINES = 40;

/**
 * The faintest a shape may be and still be drawn: 1%.
 *
 * Below it the design shows nothing a person could see, and drawing it anyway
 * is how the sketch INVENTED a shape: the one real design Loom holds paints its
 * status bar's background at alpha 0.0001, and a renderer that floored every
 * opacity at 0.05 drew a grey band across a white screen. The projector drops a
 * paint below this (a node whose own opacity chain is below it is left out with
 * its whole subtree, counted as hidden), and the renderer draws nothing for an
 * `op` below it — both ends, because the renderer also draws scenes it did not
 * project.
 */
export const SCENE_MIN_ALPHA = 0.01;

// ─── Outlines ────────────────────────────────────────────────────────────────

/**
 * The Figma node types whose OUTLINE the drawing may carry.
 *
 * The same seven words as `FIGMA_GEOMETRY_NODE_TYPES`
 * (`@loom/workflows`, `fetch-figma-design/raw-blob.ts`), which is the list the
 * read keeps `fillGeometry` / `strokeGeometry` / `relativeTransform` / `size`
 * on before the raw bytes are stored — every other node's outline is stripped
 * there, so an outline on any other type cannot reach the projector from a
 * stored row. This module may not import `@loom/workflows`, so the two literals
 * are pinned equal by `packages/workflows/test/fetch-figma-design-geometry-parity.test.ts`
 * rather than by an import.
 *
 * NOT a list verified against Figma's REST node types, and it should not be
 * read as one: `POLYGON` and `ARROW` are not REST types (the API sends
 * `REGULAR_POLYGON`, and an arrow is a `LINE` or `VECTOR` with an arrow
 * `strokeCap`). They are carried because the digest's vector family has always
 * carried them, and a word Figma never sends costs nothing.
 */
export const SCENE_GEOMETRY_TYPES: ReadonlySet<string> = new Set([
  "VECTOR",
  "BOOLEAN_OPERATION",
  "STAR",
  "POLYGON",
  "REGULAR_POLYGON",
  "LINE",
  "ARROW",
]);

/**
 * The longest one outline string may be — `d` and `sd` each.
 *
 * Counted AFTER the projector re-spells the path with every number rounded to
 * one decimal, so the cap measures shape, not Figma's float spelling (a raw
 * `fillGeometry` path writes `12.000000953674316` where a sketch needs `12`).
 * 2,000 characters is a detailed icon; a vector over it is drawn as a plain
 * box and counted, which is what the caption's "too big to keep" means.
 */
export const SCENE_MAX_PATH_CHARS = 2000;

/**
 * The most outline characters one DRAWING may carry, all nodes together.
 *
 * A hard cap on what outlines can add to `app.session_events`: a scene rides
 * the `figma_scene` event, that table re-encrypts the whole session array on
 * every flush, and its FIFO cap counts events, not bytes. Before outlines a
 * scene was ~3.3 KB typical and ~73 KB at {@link SCENE_MAX_NODES}; this bounds
 * the outlines' share at 32 KB whatever the design. Spent in paint order — the
 * first shapes on the canvas keep their outlines, and a shape past the budget
 * is a plain box, counted, never a silent gap.
 */
export const SCENE_MAX_PATH_TOTAL_CHARS = 32000;

/**
 * The ONE alphabet an outline may be spelled in: SVG path commands, digits,
 * the decimal point, signs, the exponent marker and separators.
 *
 * The outline's half of the rule {@link SCENE_HEX_RE} is for colours, with the
 * same two ends: the schema pins it, and the renderer re-tests it before the
 * string touches a `d` attribute. Nothing that could open a tag, a function
 * call, a fragment or a scheme can pass it. Lookbehind-free, like everything in
 * this module.
 */
export const SCENE_PATH_RE = /^[MmZzLlHhVvCcSsQqTtAa0-9 .,+\-eE]+$/;

/** The client-side half of the outline guard: the alphabet AND the per-string
 *  cap. Call it before the value reaches a `d` attribute, even on a payload
 *  that already parsed. */
export const isScenePath = (value: unknown): value is string =>
  typeof value === "string" &&
  value.length > 0 &&
  value.length <= SCENE_MAX_PATH_CHARS &&
  SCENE_PATH_RE.test(value);

/**
 * The largest magnitude one entry of an outline's matrix {@link SceneNode.m}
 * may have. A rotation or a flip is within ±1; ten admits a stretched vector
 * and refuses the matrix a corrupt blob would use to fling a path across the
 * column.
 */
export const SCENE_MAX_TRANSFORM = 10;

/** The most colour stops one gradient may carry. More are thinned evenly,
 *  keeping the first and the last. */
export const SCENE_MAX_GRADIENT_STOPS = 8;

/** The range a gradient handle may sit in, in the node's own 0..1 space. Figma
 *  lets a handle leave the box; a sketch lets it go a whole box further on
 *  either side and no further. */
export const SCENE_GRADIENT_HANDLE_MIN = -1;
export const SCENE_GRADIENT_HANDLE_MAX = 2;

/**
 * The box an outline's local space lands on after its matrix, relative to the
 * local origin.
 *
 * `m` is SVG `matrix()` order (`[a, b, c, d]`: a point maps to
 * `(a*x + c*y, b*x + d*y)`), and the four corners of the local `sw` x `sh` box
 * are mapped and bounded. ONE function for both ends: the projector uses it to
 * check that the matrix it composed reproduces the axis-aligned box Figma
 * itself reported (and refuses the outline when it does not), and the renderer
 * uses it to find where the local origin sits inside that box — `min` is the
 * offset from the origin to the box's top-left, which is why a renderer places
 * the origin at `boxLeft - minX, boxTop - minY` and needs no translation from
 * the payload.
 */
export const sceneLocalBoxBounds = (
  m: readonly [number, number, number, number],
  sw: number,
  sh: number,
): { minX: number; minY: number; maxX: number; maxY: number } => {
  const [a, b, c, d] = m;
  const xs = [0, a * sw, c * sh, a * sw + c * sh];
  const ys = [0, b * sw, d * sh, b * sw + d * sh];
  return {
    minX: Math.min(...xs),
    minY: Math.min(...ys),
    maxX: Math.max(...xs),
    maxY: Math.max(...ys),
  };
};

// ─── Colour ──────────────────────────────────────────────────────────────────

/**
 * The ONE spelling a colour may have on this wire: `#rrggbb`, lower case.
 *
 * Not a style preference — a security bound with two ends. The projector emits
 * this spelling and this schema pins it, so a value like
 * `red; background:url(x)` fails the parse and never reaches an attribute; the
 * client re-tests the same regex before the string touches `fill` or `stroke`,
 * because CSP's `style-src` carries `unsafe-inline` (inline positioning is what
 * a drawing needs) and so cannot catch it there.
 *
 * Alpha is deliberately NOT in the hex. Figma's own paint alpha and paint
 * opacity ride on {@link SceneNode.op} instead, so there is exactly one
 * spelling to validate rather than two.
 */
export const SCENE_HEX_RE = /^#[0-9a-f]{6}$/;

/** The client-side half of the colour guard. Call it before the value reaches
 *  an SVG attribute, even on a payload that already parsed: that parse happened
 *  on another machine at another time. */
export const isSceneHex = (value: unknown): value is string =>
  typeof value === "string" && SCENE_HEX_RE.test(value);

// ─── Icon primitives ─────────────────────────────────────────────────────────

/**
 * The closed set of shapes Loom can draw from its OWN path data — the
 * FALLBACK for a vector whose real outline the row does not hold.
 *
 * LOOM'S OWN VOCABULARY, not the file's. Where the preview is on, the read asks
 * Figma for `geometry=paths` and a visible vector's own outline rides the
 * stored bytes, so it is drawn as a `path` from that and never matched by name
 * (a container is not name-matched either while anything under it holds an
 * outline). A row read without it — the flag off, an older row, a node whose
 * outline was too big to keep — holds no outline, and there a vector is either
 * one of these or a plain box that says so in the caption.
 *
 * The match runs server-side, on the layer NAME, against a table in
 * `./project.ts`. That is what keeps names off the wire: the id below is the
 * only thing a match produces.
 *
 * Adding a member is cheap and safe; REMOVING one is a {@link SCENE_VERSION}
 * bump, because a client drawing an id it does not know has nothing to fall
 * back to.
 */
export const ICON_PRIMITIVES = [
  "arrow-down",
  "arrow-left",
  "arrow-right",
  "arrow-up",
  "battery",
  "bell",
  "bluetooth",
  "calendar",
  "check",
  "chevron-down",
  "chevron-left",
  "chevron-right",
  "chevron-up",
  "close",
  "filter",
  "heart",
  "home",
  "lock",
  "mail",
  "menu",
  "more",
  "play",
  "plus",
  "search",
  "signal",
  "star",
  "star-filled",
  "user",
  "wifi",
] as const;

export type IconPrimitive = (typeof ICON_PRIMITIVES)[number];

/** The square every path below is drawn on. A renderer scales this box onto
 *  the node's own, so one number is the whole coordinate contract. */
export const ICON_PRIMITIVE_VIEWBOX = 24;

/** One primitive's geometry. `filled` says paint it; everything else is a
 *  stroke, drawn in the node's own fill colour with round caps and joins so
 *  the zero-length segments in `more` and `wifi` render as dots. */
export interface IconPrimitiveDef {
  /** An SVG path `d`, on a 24x24 box. */
  readonly d: string;
  /** Paint the path rather than stroke it. Only `star-filled` sets it, and it
   *  is the whole four-of-five-stars distinction. */
  readonly filled?: boolean;
}

/**
 * The path data, kept HERE rather than in the widget.
 *
 * One source of truth for a closed enum: a second copy in `apps/web` would
 * drift from this one the first time a primitive is added, and the drift would
 * be invisible — an unknown id simply draws nothing. It costs the bundle about
 * two kilobytes of string.
 */
export const ICON_PRIMITIVE_DEFS: Readonly<
  Record<IconPrimitive, IconPrimitiveDef>
> = {
  "arrow-down": { d: "M12 4v16M6 14l6 6 6-6" },
  "arrow-left": { d: "M20 12H4M10 6l-6 6 6 6" },
  "arrow-right": { d: "M4 12h16M14 6l6 6-6 6" },
  "arrow-up": { d: "M12 20V4M6 10l6-6 6 6" },
  battery: { d: "M2.5 8h15v8h-15zM19.5 11v2" },
  bell: { d: "M6 10a6 6 0 0 1 12 0v6l2 2H4l2-2zM10 20.5h4" },
  bluetooth: { d: "M7 7.5l10 9L12 21V3l5 4.5-10 9" },
  calendar: { d: "M4 6h16v14H4zM4 10h16M8 3v4M16 3v4" },
  check: { d: "M4.5 12.5l5 5 10-11" },
  "chevron-down": { d: "M6 9l6 6 6-6" },
  "chevron-left": { d: "M15 6l-6 6 6 6" },
  "chevron-right": { d: "M9 6l6 6-6 6" },
  "chevron-up": { d: "M6 15l6-6 6 6" },
  close: { d: "M6 6l12 12M18 6L6 18" },
  filter: { d: "M3.5 5.5h17l-6.5 7.5V20l-4-2.5v-4.5z" },
  heart: {
    d: "M12 20.5S3.5 15 3.5 9.2A4.7 4.7 0 0 1 12 7a4.7 4.7 0 0 1 8.5 2.2c0 5.8-8.5 11.3-8.5 11.3z",
  },
  home: { d: "M3.5 11L12 3.5 20.5 11M6 9.5V20h12V9.5" },
  lock: { d: "M6 11h12v9H6zM8.5 11V8a3.5 3.5 0 0 1 7 0v3" },
  mail: { d: "M3 6h18v12H3zM3 6.5l9 6.5 9-6.5" },
  menu: { d: "M4 7h16M4 12h16M4 17h16" },
  more: { d: "M6 12h.01M12 12h.01M18 12h.01" },
  play: { d: "M8 5l11 7-11 7z" },
  plus: { d: "M12 5v14M5 12h14" },
  search: { d: "M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14zM16 16l4.5 4.5" },
  signal: { d: "M4 20v-3M9 20v-7M14 20v-11M19 20V5" },
  star: {
    d: "M12 3.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8-4.3-4.1 5.9-.9z",
  },
  "star-filled": {
    d: "M12 3.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8-4.3-4.1 5.9-.9z",
    filled: true,
  },
  user: { d: "M12 4a4 4 0 1 0 0 8 4 4 0 0 0 0-8zM4.5 20a7.5 7.5 0 0 1 15 0" },
  wifi: {
    d: "M2.5 9a15 15 0 0 1 19 0M6 12.5a10 10 0 0 1 12 0M9.5 16a5 5 0 0 1 5 0M12 19.5h.01",
  },
};

// ─── The draw list ───────────────────────────────────────────────────────────

/**
 * What one entry IS, and the honesty spine of the whole preview.
 *
 *   box      a rectangle. Faithful for a rectangle; an APPROXIMATION for a
 *            vector, which is why a vector that lands here is counted into
 *            `counts.plainBoxes` and drawn dimmed.
 *   ellipse  a round shape — an `ELLIPSE`, or a box whose radius rounds it.
 *   line     a rule. Under two units on one axis, with extent on the other.
 *   image    a picture Loom does not hold. `GET /v1/images/:key` is outside the
 *            REST allowlist, so there is nothing to draw: an empty dashed box
 *            (an ellipse when `radius` rounds it all the way), counted.
 *   icon     one of {@link ICON_PRIMITIVES}, matched server-side on the layer
 *            name and drawn in the node's own colour. The fallback for a
 *            vector whose outline the row does not hold.
 *   path     a vector drawn from its OWN outline: `d` (the fill region) and
 *            `sd` (the stroke's outline, itself a region to fill), in the
 *            node's local space, placed by `m` / `sw` / `sh`. Faithful, and
 *            counted into `counts.outlines`. A renderer that does not know the
 *            kind draws nothing for it, which is why its arrival moved
 *            {@link SCENE_VERSION}.
 *   text     words, already wrapped into `lines` by the server.
 */
export const sceneKindSchema = z.enum([
  "box",
  "ellipse",
  "line",
  "image",
  "icon",
  "path",
  "text",
]);
export type SceneKind = z.infer<typeof sceneKindSchema>;

const coord = z
  .number()
  .finite()
  .min(-SCENE_MAX_DIMENSION)
  .max(SCENE_MAX_DIMENSION);
const extent = z.number().finite().min(0).max(SCENE_MAX_DIMENSION);
const hex = z.string().regex(SCENE_HEX_RE);
const pathData = z.string().min(1).max(SCENE_MAX_PATH_CHARS).regex(SCENE_PATH_RE);
const windingRule = z.enum(["nonzero", "evenodd"]);
const matrixEntry = z
  .number()
  .finite()
  .min(-SCENE_MAX_TRANSFORM)
  .max(SCENE_MAX_TRANSFORM);
const handle = z
  .number()
  .finite()
  .min(SCENE_GRADIENT_HANDLE_MIN)
  .max(SCENE_GRADIENT_HANDLE_MAX);

/**
 * One colour stop of a gradient. `o` is the stop's position along the
 * gradient, `c` its colour in the one spelling the wire allows, `a` its alpha
 * (the stop's own alpha times the paint's opacity), present only below 1 — the
 * same "alpha is not in the hex" rule every other colour here follows.
 */
export const sceneGradientStopSchema = z
  .object({
    o: z.number().finite().min(0).max(1),
    c: hex,
    a: z.number().finite().min(0).max(1).optional(),
  })
  .strict();

/**
 * A gradient, read off the paint Figma already sends.
 *
 * `k` is `l` (linear) or `r` (radial). Figma's DIAMOND and ANGULAR gradients
 * arrive as `r` too: SVG has no conic or diamond gradient, and a radial one in
 * the design's own colours is a truer sketch than the flat grey that stood in
 * for every gradient before its stops were read.
 *
 * `p` is Figma's first two `gradientHandlePositions`, `[x1, y1, x2, y2]`, in
 * the node's own normalised 0..1 space: start and end for a linear gradient,
 * centre and radius point for a radial one.
 */
export const sceneGradientSchema = z
  .object({
    k: z.enum(["l", "r"]),
    p: z.tuple([handle, handle, handle, handle]),
    s: z.array(sceneGradientStopSchema).min(2).max(SCENE_MAX_GRADIENT_STOPS),
  })
  .strict()
  .superRefine((g, ctx) => {
    for (let i = 1; i < g.s.length; i += 1) {
      const prev = g.s[i - 1];
      const cur = g.s[i];
      if (prev !== undefined && cur !== undefined && cur.o < prev.o) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "gradient stops must not go backwards",
          path: ["s", i, "o"],
        });
      }
    }
  });
export type SceneGradient = z.infer<typeof sceneGradientSchema>;

/**
 * One shape.
 *
 * `.strict()`, and that is load-bearing rather than tidy: it is what makes
 * "no layer name is on the wire" a parse rule instead of a promise. A future
 * field that carries a third-party string has to be added here deliberately,
 * in this file, next to this sentence.
 */
export const sceneNodeSchema = z
  .object({
    kind: sceneKindSchema,
    /** Frame-relative, already intersected with the frame box and rounded to
     *  0.1. The frame's own origin is (0,0). */
    x: coord,
    y: coord,
    w: extent,
    h: extent,
    /**
     * `#rrggbb`, lower case. Absent means "draw no fill" — `image`, whose
     * pixels Loom does not hold, is the one kind that always carries none.
     *
     * Three kinds always DO carry one, so a renderer never has to invent a
     * colour or fall back to another field:
     *
     *   text   a text node whose only paint the file switched off shows
     *          nothing on the screen, so it is not drawn at all rather than
     *          drawn in whatever colour the renderer defaults to.
     *   line   a rule is one colour, and Figma spells it as a fill (a thin
     *          rectangle) or a stroke (the line tool); the projector folds
     *          either onto this field.
     *   icon   the glyph's own colour, taken from the first paint at or below
     *          the matched node. A match with no colour anywhere below it is
     *          dropped rather than drawn.
     *
     * A node painted with a gradient carries {@link SceneNode.grad} INSTEAD —
     * text included, so "text always has a colour" reads "text always has a
     * `fill` or a `grad`".
     */
    fill: hex.optional(),
    /**
     * `#rrggbb`, lower case. Absent means "draw no stroke".
     *
     * On a `path` it is the colour `sd` is FILLED with: Figma's
     * `strokeGeometry` is the stroke's own outline, a region, so the stroke's
     * weight, caps and joins are already in its shape and a renderer never
     * strokes it.
     */
    stroke: hex.optional(),
    /**
     * Opacity, 0..1, present only when it is below 1.
     *
     * What folds into it: the paint's own alpha and opacity (the hex carries
     * no alpha), the node's own `opacity` times every ancestor's (a glyph
     * inside a 40% group is drawn at 40%), the 0.45 dimming of a vector Loom
     * holds no drawable outline for, and the 0.35 dimming of a gradient whose
     * stops could not be read. A gradient whose stops WERE read is not dimmed —
     * its colours are the design's own, and each stop carries its own alpha.
     * A node with both a fill and a stroke takes the FILL's alpha; carrying two
     * opacities for a sketch buys nothing.
     *
     * Never below {@link SCENE_MIN_ALPHA} from the projector: a shape that
     * faint is not drawn at all.
     */
    op: z.number().min(0).max(1).optional(),
    /** Corner radius, already clamped to `min(w,h)/2`. On an `image`, a
     *  radius of `min(w,h)/2` means the picture is round — an `ELLIPSE` with
     *  an image fill, or a fully rounded square — and is drawn as a dashed
     *  ellipse. */
    radius: extent.optional(),
    /**
     * Rotation in RADIANS about the node's own centre. SVG's `rotate()` takes
     * DEGREES — use {@link sceneRotateDeg} rather than converting by hand.
     *
     * Emitted for TEXT ONLY, and the omission everywhere else is deliberate:
     * Figma's `absoluteBoundingBox` is the AXIS-ALIGNED box of the already
     * rotated shape, so rotating a box drawn at those coordinates would rotate
     * it twice. Rotated TEXT is the one case where the axis-aligned box is
     * unusable on its own — horizontal words in a tall thin box read as a bug
     * — so it is rotated about the box centre, and the result is approximate
     * rather than wrong.
     */
    rot: z.number().finite().min(-7).max(7).optional(),
    /** `icon` only. */
    icon: z.enum(ICON_PRIMITIVES).optional(),
    /**
     * `text` only. Already wrapped SERVER-side to fit the box, so the client
     * emits one `<tspan>` per entry and needs no measurement, no
     * `<foreignObject>` and therefore no HTML sink anywhere in the drawing.
     */
    lines: z.array(z.string()).max(SCENE_MAX_TEXT_LINES).optional(),
    /** `text` only. Font size in frame units, AFTER the fitter shrank it. */
    size: z.number().finite().positive().max(SCENE_MAX_DIMENSION).optional(),
    /** `text` only. Line height in frame units. */
    lh: z.number().finite().positive().max(SCENE_MAX_DIMENSION).optional(),
    /** `text` only. Figma's `fontWeight`. */
    weight: z.number().int().min(1).max(1000).optional(),
    /** `text` only. Absent means left. */
    align: z.enum(["left", "center", "right"]).optional(),
    /** `text` only. Letter spacing the FITTER chose to make the words fit the
     *  box Figma reported — never a value read out of the design. */
    track: z.number().finite().min(-4).max(4).optional(),
    /**
     * A gradient paint, drawn as one. On a `box`, `ellipse`, `path` or `text`
     * it takes the place of `fill`, and it is counted into `counts.gradients`.
     */
    grad: sceneGradientSchema.optional(),
    // ── `path` only ─────────────────────────────────────────────────────────
    //
    // Everything below is SHAPE data read off the stored bytes' `fillGeometry`
    // / `strokeGeometry` / `relativeTransform` / `size`. None of it is a name;
    // see {@link SCENE_PATH_RE} for what a path string may be spelled in.
    /** The fill region: every `fillGeometry` path, joined, numbers rounded to
     *  one decimal. In the node's LOCAL unrotated space, origin at its
     *  top-left. Painted with `fill` or `grad`. */
    d: pathData.optional(),
    /** `d`'s winding rule. Absent means nonzero. */
    dr: windingRule.optional(),
    /** The stroke's outline, from `strokeGeometry`, same space as `d`. Painted
     *  with `stroke` — FILLED, never stroked. */
    sd: pathData.optional(),
    /** `sd`'s winding rule. Absent means nonzero. */
    sdr: windingRule.optional(),
    /**
     * The local space's orientation in the frame: the LINEAR part of the
     * node's transform, in SVG `matrix()` order `[a, b, c, d]` (a point maps
     * to `(a*x + c*y, b*x + d*y)`). Absent means identity.
     *
     * It is the node's own `relativeTransform` composed with the rotation of
     * every ancestor that defines a coordinate space, so a vector inside a
     * rotated instance faces the way the screen shows it. There is NO
     * translation on the wire: `x`/`y`/`w`/`h` — the box Loom already placed —
     * is the placement authority, and a renderer finds the origin with
     * {@link sceneLocalBoxBounds}.
     */
    m: z.tuple([matrixEntry, matrixEntry, matrixEntry, matrixEntry]).optional(),
    /** The local box's width and height (`size` on the Figma node). One may be
     *  zero — a `LINE` has no height — never both. */
    sw: extent.optional(),
    sh: extent.optional(),
    /**
     * Where the node's box began BEFORE it was intersected with the frame,
     * present only when that intersection moved it — a shape running off the
     * frame's left or top edge. The origin arithmetic needs the unclipped
     * corner; `x`/`y` stay the clipped one, so nothing else about the node
     * changes and the drawing still cannot leave its frame.
     */
    bx: coord.optional(),
    by: coord.optional(),
  })
  .strict();
export type SceneNode = z.infer<typeof sceneNodeSchema>;

/**
 * What the drawing is honest about, counted.
 *
 * The caption is assembled from these and NOT from a fixed sentence, which is
 * the whole difference between a promise and a measurement: on a well-made
 * design the line reads "6 shapes"; on a file of `Rectangle 47` it reads "42",
 * and the user calibrates their own trust. Designing for the worst design a
 * customer will paste, without lying about the best one.
 */
export const sceneCountsSchema = z
  .object({
    /**
     * Every node the walk VISITED in this subtree, before a single rule ran.
     *
     * Deliberately NOT `omissions.nodesIncluded` from the digest, and the two
     * will differ: the digest's number is what survived a TOKEN budget the
     * drawing does not have, and the drawing's rules (hidden subtrees, boolean
     * operands, unpainted wrappers) are not the digest's. The drawing and the
     * digest are two renderings of one tree, not two views of one number.
     */
    nodes: z.number().int().nonnegative(),
    /** Entries in `nodes` — what was actually drawn. */
    drawn: z.number().int().nonnegative(),
    /** Of those, ones that matched a primitive. */
    icons: z.number().int().nonnegative(),
    /**
     * Vector shapes for which no outline could be drawn, drawn as a dimmed
     * rectangle instead. The number behind `plural.figmaPlainBoxes`, whose
     * sentence ("couldn't draw its real outline or colours") is true of every
     * one of these reasons, so it names none of them: the row holds no outline
     * for the node (read without `geometry=paths`, or the outline was
     * stripped), or it holds one Loom would not draw — over
     * {@link SCENE_MAX_PATH_CHARS}, past the scene's
     * {@link SCENE_MAX_PATH_TOTAL_CHARS} budget, outside {@link SCENE_PATH_RE},
     * placed by a transform that does not reproduce the box Figma reported, or
     * under a turned container whose rotation unit the response gave no
     * evidence for. Also, as before, a gradient whose stops could not be read.
     */
    plainBoxes: z.number().int().nonnegative(),
    /**
     * Vectors drawn from their OWN outline (`kind: "path"`). OPTIONAL so a
     * scene from before outlines, and a hand-built fixture, still parse; the
     * projector always sets it. Not in the caption — a real outline is not a
     * compromise.
     */
    outlines: z.number().int().nonnegative().optional(),
    /** Nodes drawn with a real gradient (`grad`). Optional and caption-free
     *  for the same reasons as `outlines`. */
    gradients: z.number().int().nonnegative().optional(),
    /** Text entries. */
    texts: z.number().int().nonnegative(),
    /** Pictures Loom does not hold, drawn as an empty dashed box. The number
     *  behind `plural.figmaImagesOmitted`. */
    images: z.number().int().nonnegative(),
    /**
     * Nodes inside a subtree the FILE hides, left out entirely.
     *
     * A NODE count, like the digest's `omissions.hiddenOmitted`, so one
     * switched-off frame holding forty layers reports forty. The digest KEEPS
     * hidden layers and marks them, because a hidden layer is usually the
     * error state a ticket enumerates; a drawing has no marker that could say
     * "this is a state the screen CAN show, not what it shows now", so it
     * leaves them out and counts them instead.
     */
    hiddenOmitted: z.number().int().nonnegative(),
    /**
     * Operands of a boolean operation, skipped.
     *
     * OPTIONAL so a hand-built fixture need not invent it; the projector
     * always sets it. Not in the caption on purpose — the boolean operation
     * ITSELF is drawn, so nothing is missing from the picture. It is here
     * because the rule that produces it is the difference between one star and
     * four stacked boxes, and a rule that cannot be counted cannot be tested.
     */
    operandsSkipped: z.number().int().nonnegative().optional(),
  })
  .strict();
export type SceneCounts = z.infer<typeof sceneCountsSchema>;

/** One frame, flattened into paint order. */
export const designSceneSchema = z
  .object({
    sceneVersion: z.number().int().positive(),
    /** The frame's own box. Entries are positioned inside it. */
    w: extent,
    h: extent,
    /** Paint order: the first entry is furthest back. */
    nodes: z.array(sceneNodeSchema).max(SCENE_MAX_NODES),
    counts: sceneCountsSchema,
    /**
     * When FIGMA was read for the bytes this drawing is made of, ISO-8601.
     *
     * Not the projector's to set — it is the design store row's own `readAtMs`,
     * and there are TWO places the two meet: the emit that puts the scene in
     * the chat (`figma/preview-ask.ts`) and the serve route
     * (`http/figma-preview.ts`). On a stored scene it dates the bytes the chat
     * is carrying; on a served one it dates whatever the store holds now.
     *
     * It was declared for the second of those, and it still carries that job:
     * the drawing's cache key (`userId, figmaAccountHash, fileKey, nodeId`) has
     * no version dimension by design, so every SERVE draws the NEWEST copy Loom
     * holds. A chat with no stored scene therefore gets today's bytes for that
     * frame, and without a date on the picture nothing on screen says so.
     *
     * OPTIONAL, and it has to be declared at BOTH ends: zod strips an
     * undeclared key with no type error and no failing test, which is how the
     * `gateRefusal` and `draft` rows were lost on the way to Focus. A scene
     * with no date (an old row, a store that could not answer) parses exactly
     * as it did before.
     */
    readAtIso: z.string().min(1).max(64).optional(),
  })
  .strict()
  // The per-scene outline budget, restated at the parse. The projector spends
  // it by construction; this is what makes a draw list that did not come from
  // the projector — an injected port, a hand-built event — answer to it too.
  .superRefine((scene, ctx) => {
    let total = 0;
    for (const node of scene.nodes) {
      total += (node.d?.length ?? 0) + (node.sd?.length ?? 0);
    }
    if (total > SCENE_MAX_PATH_TOTAL_CHARS) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "the drawing's outlines are over their budget",
        path: ["nodes"],
      });
    }
  });
export type DesignScene = z.infer<typeof designSceneSchema>;

// ─── Renderer helpers ────────────────────────────────────────────────────────

/** `rot` in the unit SVG's `rotate()` actually takes. Kept here so the one
 *  conversion in the system lives next to the field that needs it. */
export const sceneRotateDeg = (node: Pick<SceneNode, "rot">): number =>
  node.rot === undefined ? 0 : (node.rot * 180) / Math.PI;

/**
 * An empty scene for a frame of a known size.
 *
 * For a host that has a box to reserve but nothing to put in it. A well-formed
 * scene rather than `null` so a renderer has one shape to handle, and every
 * count is zero so the caption says nothing rather than something false.
 */
export const emptyDesignScene = (w: number, h: number): DesignScene => ({
  sceneVersion: SCENE_VERSION,
  w,
  h,
  nodes: [],
  counts: {
    nodes: 0,
    drawn: 0,
    icons: 0,
    plainBoxes: 0,
    texts: 0,
    images: 0,
    hiddenOmitted: 0,
    operandsSkipped: 0,
    outlines: 0,
    gradients: 0,
  },
});
